import { useCallback, useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { search_bizminer_admin_kpi_model_nodes_path } from "@/routes";
import type { KpiModelNodeSearch } from "@/types/serializers";

interface Props {
  treeId?: number;
  versionId?: number;
  onSelect: (result: KpiModelNodeSearch) => void;
}

const LEVEL_LABELS: Record<number, string> = {
  1: "Pillar",
  2: "Challenge",
  3: "Criteria",
  4: "KPI",
  5: "Driver",
};

const LEVEL_COLORS: Record<number, string> = {
  1: "bg-chart-1/10 text-chart-1",
  2: "bg-chart-2/10 text-chart-2",
  3: "bg-chart-3/10 text-chart-3",
  4: "bg-chart-4/10 text-chart-4",
  5: "bg-chart-5/10 text-chart-5",
};

export default function KpiModelSearch({ treeId, versionId, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<string>("all");
  const [results, setResults] = useState<KpiModelNodeSearch[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const search = useCallback(
    async (q: string, lvl: string) => {
      if (q.length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const params = new URLSearchParams({ q });
        if (lvl !== "all") params.append("level", lvl);
        if (treeId) params.append("tree_id", treeId.toString());
        if (versionId) params.append("version_id", versionId.toString());

        const response = await fetch(
          `${search_bizminer_admin_kpi_model_nodes_path()}?${params}`,
        );
        const data: KpiModelNodeSearch[] = await response.json();
        setResults(data);
      } catch (error) {
        console.error("Search failed:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [treeId, versionId],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      search(query, level);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, level, search]);

  const handleSelect = (result: KpiModelNodeSearch) => {
    onSelect(result);
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-9 w-64 px-3 rounded-md border border-input bg-background text-sm text-left text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Search nodes...
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search nodes..."
            />
            <CommandList>
              {isLoading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
              {!isLoading && query.length >= 2 && results.length === 0 && (
                <CommandEmpty>No results found</CommandEmpty>
              )}
              {!isLoading &&
                results.map((result) => (
                  <CommandItem
                    key={result.id}
                    value={String(result.id)}
                    onSelect={() => handleSelect(result)}
                    className="flex items-start gap-2"
                  >
                    <span
                      className={cn(
                        "shrink-0 px-1.5 py-0.5 rounded text-xs font-medium",
                        LEVEL_COLORS[result.level],
                      )}
                    >
                      L{result.level}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {result.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {LEVEL_LABELS[result.level]}
                      </div>
                    </div>
                  </CommandItem>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Select value={level} onValueChange={setLevel}>
        <SelectTrigger className="h-9 w-[140px]">
          <SelectValue placeholder="All levels" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All levels</SelectItem>
          <SelectItem value="1">L1 - Pillar</SelectItem>
          <SelectItem value="2">L2 - Challenge</SelectItem>
          <SelectItem value="3">L3 - Criteria</SelectItem>
          <SelectItem value="4">L4 - KPI</SelectItem>
          <SelectItem value="5">L5 - Driver</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
